import React, { useCallback } from 'react';
import {
  Text,
} from '@fluentui/react-components';
import { Box, useFluentTokens } from '../../../../theme/fluent';
import { MarkdownRenderer } from '../../../../components/common';
import { ChatMessage as ChatMessageType } from '../../types/domain';
import { isUserMessage, isAssistantMessage, hasWidget } from '../../utils/messageUtils';
import { chatWidgetRegistry } from '../../types/widgets';
import { useChatSession } from '../../providers/ChatProvider';
import { ChatMessageFeedback } from './ChatMessageFeedback';
import { chatFeatureFlags } from '../../../../config';
import { SourcesPanel } from '../panels/SourcesPanel';
import { ReasoningPanel } from '../panels/ReasoningPanel';
import { FollowUpTags } from '../widgets/FollowUpTags';

interface ChatMessageProps {
  message: ChatMessageType;
  isLatestMessage?: boolean;
}

class WidgetErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error: Error) { console.error('[ChatMessage] widget crashed:', error); }
  render() {
    if (this.state.hasError) {
      return <Text size={200} style={{ color: 'var(--colorStatusDangerForeground1)' }}>Widget failed to render.</Text>;
    }
    return this.props.children;
  }
}

export const ChatMessage: React.FC<ChatMessageProps> = React.memo(({ message, isLatestMessage = false }) => {
  const { tokens, modeTokens } = useFluentTokens();
  const { sendWidgetResponse, sendMessage, currentSession, isChatActionDisabled, submitFeedback } = useChatSession();
  const isUser = isUserMessage(message);
  const isAssistant = isAssistantMessage(message);

  const handleWidgetAction = useCallback(async (actionType: string, actionData?: any) => {
    if (actionType === 'button_action' && actionData?.widget_id && (actionData?.button_action || actionData?.feedback_text)) {
      await sendWidgetResponse(
        actionData.widget_id,
        actionData.selected_options || [],
        actionData.button_action,
        actionData.feedback_text
      );
    }
  }, [sendWidgetResponse]);

  const handleTagClick = useCallback((prompt: string) => sendMessage(prompt), [sendMessage]);

  const renderWidget = () => {
    if (!hasWidget(message)) {
      return null;
    }

    const { widgetType, widgetData } = message;
    if (!widgetType || !widgetData) {
      return null;
    }

    const widgetDef = chatWidgetRegistry.get(widgetType);
    if (!widgetDef) {
      console.warn(`Widget type "${widgetType}" not registered`);
      return null;
    }

    const isWidgetDisabled = !isLatestMessage;

    const WidgetComponent = widgetDef.component;
    return (
      <WidgetErrorBoundary>
        <WidgetComponent
          data={widgetData}
          message={message}
          isDisabled={isWidgetDisabled}
          onAction={handleWidgetAction}
        />
      </WidgetErrorBoundary>
    );
  };

  return (
    <Box
      display="flex"
      style={{
        flexDirection: 'column',
        gap: '2px',
        alignItems: isUser ? 'flex-end' : 'flex-start',
        paddingLeft: isUser ? tokens.spacingVerticalXXXL : 0,
        paddingRight: 0,
        marginBottom: tokens.spacingVerticalS,
      }}
    >
      {/* Message Content */}
      <Box
        display="flex"
        style={{
          gap: '6px',
          alignItems: 'flex-start',
          width: isUser ? undefined : '100%',
        }}
      >
        <Box
          style={{
            paddingLeft: tokens.spacingHorizontalM,
            paddingRight: tokens.spacingHorizontalM,
            paddingTop: tokens.spacingVerticalS,
            paddingBottom: isUser ? tokens.spacingHorizontalM : tokens.spacingVerticalS,
            borderRadius: isUser ? tokens.borderRadiusLarge : '6px',
            backgroundColor: isUser ? modeTokens.colorGreenBackground : 'transparent',
            minHeight: '1px',
            minWidth: '1px',
            ...(isUser ? {} : { flex: 1 }),
          }}
        >
          {isUser ? (
            <Text
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: tokens.fontSizeBase300,
                lineHeight: tokens.lineHeightBase300,
                color: modeTokens.colorNeutralForeground1,
                fontWeight: tokens.fontWeightRegular,
              }}
            >
              {message.content}
            </Text>
          ) : (
            <>
              {/* Reasoning steps — above content, matching Figma design */}
              {message.reasoningSteps && message.reasoningSteps.length > 0 && (
                <ReasoningPanel steps={message.reasoningSteps} />
              )}
              <MarkdownRenderer
                content={message.content}
              />
            </>
          )}
        </Box>
      </Box>

      {renderWidget()}

      {/* Sources */}
      {isAssistant && message.sources && message.sources.length > 0 && (
        <SourcesPanel sources={message.sources} />
      )}

      {/* Follow-up tags */}
      {isAssistant && isLatestMessage && message.followUpTags && message.followUpTags.length > 0 && (
        <FollowUpTags
          tags={message.followUpTags}
          onTagClick={handleTagClick}
          isDisabled={isChatActionDisabled}
        />
      )}

      {chatFeatureFlags.messageFeedbackAndCopy && !isUser && !hasWidget(message) && message.content?.trim() && (
        <ChatMessageFeedback
          content={message.content}
          messageId={isAssistant ? message.messageId : undefined}
          sessionId={currentSession?.id}
          threadId={currentSession?.threadId}
          onSubmitFeedback={submitFeedback}
        />
      )}
    </Box>
  );
});
